// src/hooks/usePurchaseLayers.js
//
// Registers and synchronizes the Imagery Purchase grid layers:
//   • us48 vector source + layers (three-state: owned / available / cart)
//   • glo_ols purchase-specific layers (same three states)
//
// Block states driven by `feature.properties.status` from the tile query:
//   • "owned"     → green fill, solid outline, NOT selectable
//   • "available" → faint blue, dashed outline, selectable
//   • (cart)      → brighter blue fill, solid outline (driven by feature-state)
//
// Also syncs `setFeatureState({ selected: true })` for blocks the user has
// added to their cart, and toggles layer visibility based on purchase mode
// + active grid.
//
// Layer visibility management: when purchase mode activates, this hook hides
// non-essential layers (wells, AOIs, operating AOI, product layers) and
// restores them on exit.

import { useEffect, useRef } from "react";
import { usePurchaseStore } from "../stores/usePurchaseStore";
import { fetchWithAuth } from "../api/fetchWithAuth";
import { FP_LAYER_IDS } from "../lib/map/footprintStyle";

import { getConfig } from '../config';

const US48_SOURCE = "purchase-us48";
const OWNED_COVERAGE_SOURCE = "purchase-owned-coverage";
const US48_SOURCE_LAYER = "us48";
const GLO_OLS_PURCHASE_SOURCE = "purchase-glo-ols";
const GLO_OLS_SOURCE_LAYER = "glo_ols";

// Layers to hide when purchase mode is active.
// These are restored to their previous visibility on mode exit.
const PURCHASE_KEEP_PREFIXES = [
  "mapbox-",           // basemap
  "us48-purchase", // purchase grid layers
  "owned-coverage", // dissolved purchased region
  "glo-ols-purchase",  // purchase grid layers
  "purchase-",         // any future purchase layers
  "anchor-",           // stack manager anchors (invisible)
];

// The entitled mask and edges (footprintStyle.js) are not purchase mode's to
// save and restore: useFootprintOverlay hides them while purchase mode is on
// (REM-696) and shows them again on exit. Saving them here found them already
// hidden (MapView runs the overlay first) and wrote that back over the
// overlay's restore on exit: the mask never came back (DB's check 5, S-b).
const FOOTPRINT_LAYERS = new Set(FP_LAYER_IDS);

function isPurchaseKeepLayer(id) {
  return FOOTPRINT_LAYERS.has(id) || PURCHASE_KEEP_PREFIXES.some(p => id.startsWith(p));
}

export const PURCHASE_LAYER_IDS = {
  us48: {
    outline: "us48-purchase-outline",
    fill: "us48-purchase-fill",
  },
  // Purchased imagery is COVERAGE, not cells -- see the comment on the paint
  // expressions. Its own source because it is one dissolved polygon from an
  // API call, not a tiled grid.
  owned: {
    outline: "owned-coverage-outline",
    fill: "owned-coverage-fill",
  },
  glo_ols: {
    outline: "glo-ols-purchase-outline",
    fill: "glo-ols-purchase-fill",
  },
};

// ─── Paint expressions ───────────────────────────────────────────────
// Three visual states per block:
// CELLS ARE FOR CHOOSING; COVERAGE IS FOR OWNING.
//
// Purchased imagery no longer draws its cell breakdown. The grid answers "what
// am I buying" and stops being useful the moment the answer is settled -- 656
// squares say "this is yours" 656 times. Owned is served pre-dissolved by
// /api/v1/purchase/owned-coverage and drawn as one outline and fill. Available
// keeps every cell, because that is still the thing being chosen.
//
// The cell layers below therefore FILTER OUT status='owned' rather than styling
// it: two representations of the same region, drawn at once, would double the
// fill wherever they agree.
//
//   1. Cart (feature-state "selected" = true)  → bright blue, solid
//   2. Owned (properties.status = "owned")     → green, solid
//   3. Available (properties.status = "available") → blue, selectable
//
// OWNED reads as outline AND fill: its fill was 0.10, which against imagery is
// close enough to nothing that the block was effectively its outline alone, so
// "what do I already own" could not be answered by looking. AVAILABLE is blue
// throughout -- block, outline and border are one colour (#1f9dff) rather than
// a fill in one blue and a line in another (#60a5fa), which read as two states
// when it is one.

// AVAILABLE DRAWS ON TOP OF OWNED.
//
// Both states live in a single fill layer keyed off properties.status, so there
// is no layer order to rearrange -- within one layer MapLibre draws in feature
// order, which is tile order, which is arbitrary. sort-key is the only lever:
// features with a HIGHER key paint later, hence on top.
//
// It matters where the two meet. An owned cell and an available cell share an
// edge everywhere the purchased region ends, and whichever paints second owns
// that boundary. Leaving it to tile order means the seam moves between tiles.
function makeSortKey() {
  return [
    "case",
    ["==", ["get", "status"], "owned"],
    0,
    1,
  ];
}

function makeFillPaint() {
  return {
    "fill-color": [
      "case",
      // Cart selection overrides everything
      ["boolean", ["feature-state", "selected"], false],
      "#1f9dff",                          // blue — in cart
      ["==", ["get", "status"], "owned"],
      "#22c55e",                          // green — already purchased
      "#1f9dff",                          // blue — available
    ],
    "fill-opacity": [
      "case",
      ["boolean", ["feature-state", "selected"], false],
      // A SHADE DARKER THAN AVAILABLE, NOT LIGHTER. This was 0.20 against
      // available's 0.25, so adding a block to the cart made its fill FAINTER
      // and read as the fill being removed -- the one action whose feedback has
      // to be unmistakable was the one that looked like an erase. 0.45 was
      // the first correction and still read as close to available; 0.60 is
      // the one that separates them at a glance over bright imagery.
      0.60,                                // cart — darker than available
      ["==", ["get", "status"], "owned"],
      0.20,                               // owned — filtered out here; see below
      0.25,                               // available
    ],
  };
}

function makeLinePaint() {
  return {
    "line-color": [
      "case",
      ["boolean", ["feature-state", "selected"], false],
      "#1f9dff",                          // blue — in cart
      ["==", ["get", "status"], "owned"],
      "#22c55e",                          // green — owned
      "#1f9dff",                          // blue — available, same as its fill
    ],
    "line-width": [
      "case",
      ["boolean", ["feature-state", "selected"], false],
      2.0,
      ["==", ["get", "status"], "owned"],
      1.2,
      1.2,                                 // available — a border, not a hairline
    ],
    "line-opacity": [
      "case",
      ["boolean", ["feature-state", "selected"], false],
      1.0,
      ["==", ["get", "status"], "owned"],
      0.7,
      0.9,                                 // available — the selectable state
    ],
    "line-dasharray": [
      "case",
      // Solid for cart and owned, dashed for available
      ["boolean", ["feature-state", "selected"], false],
      ["literal", [1]],                   // solid
      ["==", ["get", "status"], "owned"],
      ["literal", [1]],                   // solid
      ["literal", [3, 2]],               // dashed
    ],
  };
}

// ─── Hook ────────────────────────────────────────────────────────────

export function usePurchaseLayers({ map, mapLoaded, addLayer, purchaseActive }) {
  const activeGrid = usePurchaseStore(s => s.activeGrid);
  const selected = usePurchaseStore(s => s.selected);

  // Track which feature states we've set so we can clear them on change.
  const appliedRef = useRef(new Map());

  // Track previous layer visibility so we can restore on mode exit.
  const savedVisibilityRef = useRef(null);

  /* ──── register sources + layers once ──── */
  useEffect(() => {
    if (!map || !mapLoaded) return;
    // console.log("[purchaseLayers] registering sources");

    // NO org_id in the tile URL. The org is taken from the bearer token that
    // mapTransformRequest attaches to every /vtiles/ request, and the server
    // ignores the query param — it was previously the ONLY thing naming the
    // org, which meant any caller could ask for any org's entitlement map.
    //
    // It also never worked as a cache key: this effect depends on [map,
    // mapLoaded, addLayer] only, so the source is registered once and an org
    // switch would not have rebuilt the URL anyway.

    if (!map.getSource(US48_SOURCE)) {
      // console.log("[purchaseLayers] adding source:", US48_SOURCE);
      map.addSource(US48_SOURCE, {
        type: "vector",
        tiles: [
          `${window.location.origin}/vtiles/us48/{z}/{x}/{y}.mvt`,
        ],
        minzoom: 5,
        maxzoom: 18,
      });
    }

    // --- purchased coverage (dissolved) ---
    //
    // ADDED FIRST, AND THAT IS THE Z-ORDER. MapLibre draws layers in insertion
    // order, so registering owned before the cell layers puts available on top
    // of purchased -- which is what the sort-key does WITHIN the cell layer and
    // could not do ACROSS layers, because owned now lives in a different one.
    if (!map.getSource(OWNED_COVERAGE_SOURCE)) {
      map.addSource(OWNED_COVERAGE_SOURCE, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
    }

    if (!map.getLayer(PURCHASE_LAYER_IDS.owned.fill)) {
      addLayer({
        group: "purchase",
        layerConfig: {
          id: PURCHASE_LAYER_IDS.owned.fill,
          type: "fill",
          source: OWNED_COVERAGE_SOURCE,
          layout: { visibility: "none" },
          paint: { "fill-color": "#22c55e", "fill-opacity": 0.20 },
        },
      });
    }

    if (!map.getLayer(PURCHASE_LAYER_IDS.owned.outline)) {
      addLayer({
        group: "purchase",
        layerConfig: {
          id: PURCHASE_LAYER_IDS.owned.outline,
          type: "line",
          source: OWNED_COVERAGE_SOURCE,
          layout: { visibility: "none" },
          paint: { "line-color": "#22c55e", "line-width": 1.6, "line-opacity": 0.9 },
        },
      });
    }

    // --- us48 layers ---
    if (!map.getLayer(PURCHASE_LAYER_IDS.us48.fill)) {
      addLayer({
        group: "purchase",
        layerConfig: {
          id: PURCHASE_LAYER_IDS.us48.fill,
          type: "fill",
          source: US48_SOURCE,
          "source-layer": US48_SOURCE_LAYER,
          minzoom: 6,
          layout: { visibility: "none", "fill-sort-key": makeSortKey() },
          // Owned is drawn as dissolved coverage, so its cells must not also
          // paint here. `!=` keeps features whose status is ABSENT too, which
          // is the anonymous-tile case -- no org, no ownership claim, still a
          // grid to look at.
          filter: ["!=", ["get", "status"], "owned"],
          paint: makeFillPaint(),
        },
      });
    }

    if (!map.getLayer(PURCHASE_LAYER_IDS.us48.outline)) {
      addLayer({
        group: "purchase",
        layerConfig: {
          id: PURCHASE_LAYER_IDS.us48.outline,
          type: "line",
          source: US48_SOURCE,
          "source-layer": US48_SOURCE_LAYER,
          minzoom: 6,
          layout: { visibility: "none", "line-sort-key": makeSortKey() },
          // Owned is drawn as dissolved coverage, so its cells must not also
          // paint here. `!=` keeps features whose status is ABSENT too, which
          // is the anonymous-tile case -- no org, no ownership claim, still a
          // grid to look at.
          filter: ["!=", ["get", "status"], "owned"],
          paint: makeLinePaint(),
        },
      });
    }

    // --- glo_ols purchase source (separate, with purchase_mode param) ---
    if (!map.getSource(GLO_OLS_PURCHASE_SOURCE)) {
      map.addSource(GLO_OLS_PURCHASE_SOURCE, {
        type: "vector",
        tiles: [
          `${window.location.origin}/vtiles/glo_ols/{z}/{x}/{y}.mvt?purchase_mode=1`,
        ],
        minzoom: 6,
        maxzoom: 16,
      });
    }

    if (!map.getLayer(PURCHASE_LAYER_IDS.glo_ols.fill)) {
      addLayer({
        group: "purchase",
        layerConfig: {
          id: PURCHASE_LAYER_IDS.glo_ols.fill,
          type: "fill",
          source: GLO_OLS_PURCHASE_SOURCE,
          "source-layer": GLO_OLS_SOURCE_LAYER,
          minzoom: 6,
          layout: { visibility: "none", "fill-sort-key": makeSortKey() },
          filter: ["!=", ["get", "status"], "owned"],
          paint: makeFillPaint(),
        },
      });
    }

    if (!map.getLayer(PURCHASE_LAYER_IDS.glo_ols.outline)) {
      addLayer({
        group: "purchase",
        layerConfig: {
          id: PURCHASE_LAYER_IDS.glo_ols.outline,
          type: "line",
          source: GLO_OLS_PURCHASE_SOURCE,
          "source-layer": GLO_OLS_SOURCE_LAYER,
          minzoom: 6,
          layout: { visibility: "none", "line-sort-key": makeSortKey() },
          filter: ["!=", ["get", "status"], "owned"],
          paint: makeLinePaint(),
        },
      });
    }
  }, [map, mapLoaded, addLayer]);

  /* ──── toggle grid layer visibility ──── */
  useEffect(() => {
    if (!map || !mapLoaded) return;

    const setVis = (id, visible) => {
      if (!map.getLayer(id)) return;
      map.setLayoutProperty(id, "visibility", visible ? "visible" : "none");
    };

    const us48Visible = purchaseActive && activeGrid === "us48";
    const gloVisible = purchaseActive && activeGrid === "glo_ols";

    setVis(PURCHASE_LAYER_IDS.us48.fill, us48Visible);
    setVis(PURCHASE_LAYER_IDS.us48.outline, us48Visible);
    setVis(PURCHASE_LAYER_IDS.glo_ols.fill, gloVisible);
    setVis(PURCHASE_LAYER_IDS.glo_ols.outline, gloVisible);

    // Purchased coverage is GRID-AGNOSTIC: it is the org's owned region, not a
    // reading of whichever grid is selected, so it shows whenever purchase mode
    // is on rather than following activeGrid.
    setVis(PURCHASE_LAYER_IDS.owned.fill, purchaseActive);
    setVis(PURCHASE_LAYER_IDS.owned.outline, purchaseActive);
  }, [map, mapLoaded, purchaseActive, activeGrid]);

  /* ──── hide/restore other layers on mode enter/exit ──── */
  useEffect(() => {
    if (!map || !mapLoaded) return;

    if (purchaseActive) {

      // Save and hide ALL layers except basemap + purchase layers.
      const saved = {};

      // Swap basemap to satellite
      const basemapSource = map.getSource("mapbox-satellite");
      if (basemapSource?.setTiles) {
        const mapboxToken = getConfig().mapboxToken;
        saved.__basemapTiles = basemapSource.tiles?.slice();
        basemapSource.setTiles([
          `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v11/tiles/256/{z}/{x}/{y}@2x?access_token=${mapboxToken}`
        ]);
      }

      const allLayers = map.getStyle().layers || [];
      for (const layer of allLayers) {
        if (isPurchaseKeepLayer(layer.id)) continue;
        const currentVis = map.getLayoutProperty(layer.id, "visibility") ?? "visible";
        saved[layer.id] = currentVis;
        if (currentVis !== "none") {
          map.setLayoutProperty(layer.id, "visibility", "none");
        }
      }
      savedVisibilityRef.current = saved;
    } else {
      // Restore previous visibility.
      const saved = savedVisibilityRef.current;
      if (saved) {

        // Restore basemap
        const basemapSource = map.getSource("mapbox-satellite");
        if (basemapSource?.setTiles && saved.__basemapTiles) {
          basemapSource.setTiles(saved.__basemapTiles);
          delete saved.__basemapTiles;
        }

        for (const [layerId, vis] of Object.entries(saved)) {
          if (map.getLayer(layerId)) {
            map.setLayoutProperty(layerId, "visibility", vis);
          }
        }
        savedVisibilityRef.current = null;
      }
    }
  }, [map, mapLoaded, purchaseActive]);

  /* ──── load the dissolved purchased region ──── */
  //
  // Fetched, not tiled: it is one polygon of a few KB (measured 3.5 KB for 656
  // cells on dev), and tiling it would mean re-dissolving per tile.
  //
  // Keyed on purchaseActive so it refreshes each time the mode is entered,
  // which is the point at which a purchase completed elsewhere would otherwise
  // still be showing as available.
  useEffect(() => {
    if (!map || !mapLoaded || !purchaseActive) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await fetchWithAuth("/api/v1/purchase/owned-coverage");
        if (!res.ok || cancelled) return;
        const fc = await res.json();
        if (cancelled) return;
        const src = map.getSource(OWNED_COVERAGE_SOURCE);
        if (src) src.setData(fc);
      } catch {
        // Leave the source empty. Showing nothing as purchased is wrong but
        // safe; showing the wrong region as purchased is neither.
      }
    })();

    return () => { cancelled = true; };
  }, [map, mapLoaded, purchaseActive]);

  /* ──── sync selection → feature state ──── */
  useEffect(() => {
    if (!map || !mapLoaded) return;

    // Clear previously-applied feature states that are no longer selected.
    for (const [key, ref] of appliedRef.current.entries()) {
      if (!selected.has(key)) {
        map.setFeatureState(
          { source: ref.source, sourceLayer: ref.sourceLayer, id: ref.id },
          { selected: false }
        );
        appliedRef.current.delete(key);
      }
    }

    // Apply feature state for every currently-selected entry.
    for (const [key, entry] of selected.entries()) {
      if (appliedRef.current.has(key)) continue;
      const source = entry.grid === "us48"
        ? US48_SOURCE
        : GLO_OLS_PURCHASE_SOURCE;
      const sourceLayer = entry.grid === "us48"
        ? US48_SOURCE_LAYER
        : GLO_OLS_SOURCE_LAYER;

      map.setFeatureState(
        { source, sourceLayer, id: entry.id },
        { selected: true }
      );
      appliedRef.current.set(key, { source, sourceLayer, id: entry.id });
    }
  }, [map, mapLoaded, selected]);
}
